// src/pages/TicketDesignTemplate/modals/FilePreviewModal.tsx
import React, { useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { fetchTicketDesignTemplateFileUrl } from 'slices/ticketDesignTemplate/thunk';
import { clearFileUrl } from 'slices/ticketDesignTemplate/reducer';
import { TicketDesignTemplate } from '../types';
import TicketLayoutEditor from '../components/TicketLayoutEditor';
import { toast } from 'react-toastify';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_design_template');

interface FilePreviewModalProps {
  isOpen: boolean;
  template: TicketDesignTemplate;
  onClose: () => void;
}

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  isOpen,
  template,
  onClose,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { fileUrl, loadingFileUrl } = useSelector((state: any) => state.TicketDesignTemplate);

  // Fetch Signed URL khi modal mở
  useEffect(() => {
    if (isOpen && template.id) {
      dispatch(fetchTicketDesignTemplateFileUrl(template.id) as any);
    }

    // Cleanup: clear fileUrl khi đóng modal
    return () => {
      dispatch(clearFileUrl());
    };
  }, [isOpen, template.id, dispatch]);

  // Format file size
  const formatFileSize = (bytes?: number | null) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Check if file is image
  const isImage = template.fileType?.startsWith('image/');

  // Handle download
  const handleDownload = () => {
    if (fileUrl) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = template.fileName || 'file';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(st('dang_tai_xuong_file_message'));
    }
  };

  // Render preview content
  const renderPreview = () => {
    if (loadingFileUrl) {
      return (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-3 text-muted">{st('dang_tai_file_text')}</p>
        </div>
      );
    }

    if (!fileUrl) {
      return (
        <div className="text-center py-5">
          <i className="ri-file-warning-line" style={{ fontSize: '48px', color: '#f1b44c' }}></i>
          <p className="mt-3 text-muted">{st('khong_the_tai_file_vui_long_text')}</p>
        </div>
      );
    }

    if (isImage) {
      return (
        <TicketLayoutEditor
          imageUrl={fileUrl}
          initialLayoutJson={template.layoutJson}
          readOnly
        />
      );
    }

    // For PDF files
    return (
      <div style={{ height: '600px' }}>
        <iframe
          src={fileUrl}
          title={template.fileName || 'Preview'}
          style={{ width: '100%', height: '100%', border: 'none' }}
          onError={() => toast.error(st('khong_the_hien_thi_file_pdf_message'))}
        />
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" style={{ maxWidth: '90%' }}>
      <ModalHeader toggle={onClose}>
        {st('xem_truoc_file_title')} {template.fileName}
      </ModalHeader>
      <ModalBody>
        {/* File info */}
        <div className="alert alert-info mb-3">
          <div className="row">
            <div className="col-md-3">
              <strong>{st('ten_file_text')}</strong> {template.fileName}
            </div>
            <div className="col-md-2">
              <strong>{st('kich_thuoc_text')}</strong> {formatFileSize(template.fileSize)}
            </div>
            <div className="col-md-2">
              <strong>{st('loai_text')}</strong> {template.fileType}
            </div>
            <div className="col-md-3">
              <strong>{st('ma_mau_text_2')}</strong> {template.templateCode}
            </div>
            <div className="col-md-2">
              <strong>{st('nguoi_tai_text')}</strong> {template.uploadedByName || '-'}
            </div>
          </div>
        </div>

        {/* Preview content */}
        <div className="border rounded p-2" style={{ backgroundColor: '#f8f9fa', minHeight: '400px' }}>
          {renderPreview()}
        </div>

        {/* Security notice */}
        <div className="alert alert-warning mt-3 mb-0">
          <i className="ri-shield-line"></i> <strong>{st('luu_y_bao_mat_text')}</strong>
          <ul className="mb-0 mt-2">
            <li>{st('url_xem_truoc_co_thoi_han_text')}</li>
            <li>{st('dong_modal_nay_se_huy_url_text')}</li>
            <li>{st('mo_lai_modal_se_tao_url_text')}</li>
          </ul>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>
          {st('close_button')}
        </Button>
        {fileUrl && PageActionPermission.canDisplay(location.pathname, "button-download") && (
          <Button
            color="primary"
            data-action-code="button-download"
            data-action-name="Tải xuống file mẫu vé"
            onClick={handleDownload}
          >
            <i className="ri-download-line"></i> {st('tai_xuong_button')}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default FilePreviewModal;